/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';
import { REQUEST_STATUS_COMPLETED } from "../../../../share/util/api/polling";
import individualDiagnose from '../../../../share/util/individualDiagnose';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import error from '../../../../common/nodejs/errors';

/** 各フェーズ定義 */
const PHASE = {
  START: "start",                                 // 初期表示
  ACTIVATING: "activating",                       // AssemblyMode切り替え表示
  DEACTIVATING: "deactivating",                   // AssemblyMode解除表示
  ACTIVATING_SUCCESS: "activatingSuccess",        // AssemblyMode切り替え成功表示
  DEACTIVATING_SUCCESS: "deactivatingSuccess",    // AssemblyMode解除成功表示
  ERROR: "error",                                 // 共通エラー表示
  START_CONDITION_ERROR: "startConditionError",   // 実行条件不成立表示
  ACTIVATING_ERROR: "activatingError",            // AssemblyMode切り替え失敗表示
  DEACTIVATING_ERROR: "deactivatingError",        // AssemblyMode解除失敗表示
};

// モード: 切り替え/解除/その他
const MODE_PROPERTY = {
  SWITCH: "switch",
  CANCEL: "cancel",
  OTHER: "other"
};

export default {
  name: "requirement159",
  data() {
    return {
      phase: PHASE.START,
      isDispose: true,
      isLoading: false,
      systemId: "",
      mode: "",
      modal_requirement_159: false,
      exitPromise: null
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    // ダイアログのヘッダー色
    headerColor() {
      switch (this.phase) {
        case PHASE.ERROR:
        case PHASE.ACTIVATING_ERROR:
        case PHASE.DEACTIVATING_ERROR:
        case PHASE.START_CONDITION_ERROR:
          return "danger";
        case PHASE.ACTIVATING_SUCCESS:
        case PHASE.DEACTIVATING_SUCCESS:
          return "success";
        case PHASE.ACTIVATING:
        case PHASE.DEACTIVATING:
        default:
          return "secondary";
      }
    },

    // ダイアログヘッダタイトル
    headerTitle() {
      // 確認
      const confirmationTitle = 'message.common_title_confirmation';
      // 成功
      const successTitle = 'message.common_title_success';
      // エラー
      const errorTitle = 'message.common_title_error';

      const titleMap = {
        [PHASE.START]: confirmationTitle,
        [PHASE.ACTIVATING]: confirmationTitle,
        [PHASE.DEACTIVATING]: confirmationTitle,
        [PHASE.ACTIVATING_SUCCESS]: successTitle,
        [PHASE.DEACTIVATING_SUCCESS]: successTitle,
        [PHASE.ERROR]: errorTitle,
        [PHASE.START_CONDITION_ERROR]: errorTitle,
        [PHASE.ACTIVATING_ERROR]: errorTitle,
        [PHASE.DEACTIVATING_ERROR]: errorTitle,
      };
      return this.$t(titleMap[this.phase]);
    },

    // ダイアログボディメッセージ
    workSupportMessage() {
      const messageMap = {
        [PHASE.START]: '',
        [PHASE.ACTIVATING]: `${this.i18nWsKey}.REQ_WS_159_TXT_1`,
        [PHASE.DEACTIVATING]: `${this.i18nWsKey}.REQ_WS_159_TXT_2`,
        [PHASE.ACTIVATING_SUCCESS]: `${this.i18nWsKey}.REQ_WS_159_TXT_3`,
        [PHASE.DEACTIVATING_SUCCESS]: `${this.i18nWsKey}.REQ_WS_159_TXT_4`,
        [PHASE.START_CONDITION_ERROR]: `${this.i18nWsKey}.REQ_WS_159_TXT_5`,
        [PHASE.ACTIVATING_ERROR]: `${this.i18nWsKey}.REQ_WS_159_TXT_6`,
        [PHASE.DEACTIVATING_ERROR]: `${this.i18nWsKey}.REQ_WS_159_TXT_7`,
        [PHASE.ERROR]: 'individual.error_communication_error',
      };
      return this.$te(messageMap[this.phase]) ? this.$t(messageMap[this.phase]) : messageMap[this.phase];
    },

    // キャンセルボタン表示
    showButtonCancel() {
      const showCancelButtonMap = {
        [PHASE.START]: false,
        [PHASE.ACTIVATING]: true,
        [PHASE.DEACTIVATING]: true,
        [PHASE.ACTIVATING_SUCCESS]: false,
        [PHASE.DEACTIVATING_SUCCESS]: false,
        [PHASE.START_CONDITION_ERROR]: false,
        [PHASE.ACTIVATING_ERROR]: false,
        [PHASE.DEACTIVATING_ERROR]: false,
        [PHASE.ERROR]: false,
      };
      let result = showCancelButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    // OKボタン表示
    showButtonOk() {
      // 初期表示以外では表示
      return this.phase !== PHASE.START;
    }
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    /** 初期化処理 */
    init() {
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      this.modal_requirement_159 = true;
      this.read();
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit("close-wizard");
      this.modal_requirement_159 = false;
    },

    /** 共通エラー */
    async showUnexpectedError() {
      // スピナー消えてしまうのを防ぐためPromiseを保持し、各API失敗時に解決を待つ
      this.exitPromise = this.isDispose ? this.dispose() : this.terminate();
      await this.exitPromise;
      this.phase = PHASE.ERROR;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     * @param {string} calledFrom 呼び出し元
     */
    async validateErrorResult(result, calledFrom = '') {
      await apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        async (result) => {
          result;
          // terminate,disposeから呼ばれた場合は共通エラーに遷移しない
          if (calledFrom === 'terminate' || calledFrom === 'dispose') return;
          await this.showUnexpectedError();
        },
      );
    },

    /** キャンセルボタン押下時処理 */
    async onCancelClicked() {
      await this.terminate();
      this.closeWizard();
    },

    /** OKボタン押下時処理 */
    onOkClicked() {
      switch (this.phase) {
        case PHASE.ACTIVATING:
        case PHASE.DEACTIVATING:
          this.write();
          break;
        case PHASE.ACTIVATING_SUCCESS:
        case PHASE.DEACTIVATING_SUCCESS:
        case PHASE.START_CONDITION_ERROR:
        case PHASE.ACTIVATING_ERROR:
        case PHASE.DEACTIVATING_ERROR:
        case PHASE.ERROR:
          this.closeWizard();
          break;
        default:
          break;
      }
    },

    /** データ読出し(セッション遷移を伴う) */
    async read() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read',
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          async (errorType) => { errorType; await this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          async (result) => { await this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          async (result) => { await this.validateErrorResult(result); },
        );

        if (!response) {
          // pollingWspApiはエラーコールバックの終了を待たずnullを返す
          // 非同期処理を含んでいるshowUnexpectedErrorが呼ばれた場合、スピナー消えてしまうためPromiseの解決を待つ
          if (this.exitPromise) await this.exitPromise;
          return;
        }

        this.isDispose = response.data?.user_data?.is_dispose;
        if (response.data?.status === REQUEST_STATUS_COMPLETED) {
          this.mode = response.data?.user_data?.mode;
          switch (this.mode) {
            case MODE_PROPERTY.SWITCH:
              this.phase = PHASE.ACTIVATING;
              break;
            case MODE_PROPERTY.CANCEL:
              this.phase = PHASE.DEACTIVATING;
              break;
            case MODE_PROPERTY.OTHER:
            default:
              await this.showUnexpectedError();
          }
        } else {
          if (response.data?.async_code === error.WSP_159_SESSION_ERROR) {
            await this.dispose();
            this.phase = PHASE.START_CONDITION_ERROR;
          } else {
            await this.showUnexpectedError();
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** データ書込み */
    async write() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: `write-${this.mode}`,
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          async (errorType) => { errorType; await this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          async (result) => { await this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          async (result) => { await this.validateErrorResult(result); },
        );

        if (!response) {
          // pollingWspApiはエラーコールバックの終了を待たずnullを返す
          // 非同期処理を含んでいるshowUnexpectedErrorが呼ばれた場合、スピナー消えてしまうためPromiseの解決を待つ
          if (this.exitPromise) await this.exitPromise;
          return;
        }

        await this.terminate();

        if (response.data?.status === REQUEST_STATUS_COMPLETED) {
          if (this.mode === MODE_PROPERTY.SWITCH) {
            this.phase = PHASE.ACTIVATING_SUCCESS;
          } else if (this.mode === MODE_PROPERTY.CANCEL) {
            this.phase = PHASE.DEACTIVATING_SUCCESS;
          }
        } else {
          if (this.mode === MODE_PROPERTY.SWITCH) {
            this.phase = PHASE.ACTIVATING_ERROR;
          } else if (this.mode === MODE_PROPERTY.CANCEL) {
            this.phase = PHASE.DEACTIVATING_ERROR;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 終了処理 */
    async terminate() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'terminate'
          },
          // エラー発生のコールバックを実装
          undefined,
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          async (result) => { await this.validateErrorResult(result, 'terminate'); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          async (result) => { await this.validateErrorResult(result, 'terminate'); },
        );
      } finally {
        this.isLoading = false;
      }
    },

    /** dispose */
    async dispose() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'dispose',
          },
          // エラー発生のコールバックを実装
          undefined,
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          async (result) => { await this.validateErrorResult(result, 'dispose'); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          async (result) => { await this.validateErrorResult(result, 'dispose'); },
        );
      } finally {
        this.isLoading = false;
      }
    }
  },

  mounted() {
    this.init();
  }
};